/**
 * Display-only translation of the FIXED English labels the chatbot API puts into
 * table and chart blocks (neo-rag api/src/services/chatBlocks.ts and chatCharts.ts):
 * block titles, column headers, enum badges, chart titles, units, slice labels and
 * the numeric notes.
 *
 * [2026-10-29] Front end only. The API still sends English; this maps a label to the
 * UI language at render time. Anything not recognised — vendor names, categories,
 * news sources, free text — is returned unchanged, and English is never touched, so
 * sorting, copying and the data itself are unaffected.
 */

const EXACT_KEYS = {
  // table titles
  'What they run today': 'chatTblWhatTheyRunToday',
  'What changed recently': 'chatTblWhatChangedRecently',
  // column headers
  'Category': 'chatColCategory',
  'Vendor': 'chatColVendor',
  'How they hold it': 'chatColHowTheyHoldIt',
  'Deployment': 'chatColDeployment',
  'Sentiment': 'chatColSentiment',
  'Evidence': 'chatColEvidence',
  'Date': 'chatColDate',
  'What happened': 'chatColWhatHappened',
  'Source': 'chatColSource',
  // enum badges (vendor_state / contract_scope / incumbent_sentiment)
  'Commercial product': 'chatBadgeCommercialProduct',
  'Native to their LMS': 'chatBadgeNativeToLms',
  'Built in-house': 'chatBadgeBuiltInHouse',
  'Open source': 'chatBadgeOpenSource',
  'Nothing in place': 'chatBadgeNothingInPlace',
  'Institution-wide': 'chatBadgeInstitutionWide',
  'Several units': 'chatBadgeSeveralUnits',
  'One unit': 'chatBadgeOneUnit',
  'Pilot': 'chatBadgePilot',
  'Positive': 'chatBadgePositive',
  'Neutral': 'chatBadgeNeutral',
  'Negative': 'chatBadgeNegative',
  // chart titles, units and slice labels
  'Which LMS the market runs': 'chatChartWhichLms',
  'Financial headroom across the market': 'chatChartFinancialHeadroom',
  'AI readiness across the market': 'chatChartAiReadiness',
  'How the market splits': 'chatChartMarketSplit',
  'institutions': 'chatUnitInstitutions',
  'accounts': 'chatUnitAccounts',
  'Low': 'chatSliceLow',
  'Moderate': 'chatSliceModerate',
  'High': 'chatSliceHigh',
  'Other': 'chatSliceOther',
  'Priority now': 'chatSlicePriorityNow',
  'In your market': 'chatSliceInYourMarket',
  'Rest of market': 'chatSliceRestOfMarket',
  'Adjacent market': 'chatSliceAdjacentMarket',
  'date unknown': 'chatDateUnknown',
};

const NUM = '(\\d[\\d,.]*)';
const PATTERNS = [
  [new RegExp('^Who holds (.+) across the market$'), 'chatChartWhoHolds', ['cat']],
  [new RegExp('^Level (\\d+)$'), 'chatSliceLevel', ['n']],
  [new RegExp(`^${NUM} of ${NUM} addressable institutions have a recorded (.+) vendor\\. Vendors below 5% are grouped as Other\\.$`), 'chatNoteVendor', ['n', 'total', 'cat']],
  [new RegExp(`^${NUM} of ${NUM} addressable institutions have a recorded LMS\\. Grouped into families, so Blackboard counts as Anthology and Canvas as Instructure\\. Open LMS and Totara are kept separate from Moodle\\.$`), 'chatNoteLms', ['n', 'total']],
  [new RegExp(`^${NUM} of ${NUM} addressable institutions carry a capacity band\\.$`), 'chatNoteCapacity', ['n', 'total']],
  [new RegExp(`^${NUM} of ${NUM} addressable institutions are scored for AI readiness\\.$`), 'chatNoteReadiness', ['n', 'total']],
  [new RegExp(`^${NUM} accounts placed\\.$`), 'chatNoteMix', ['n']],
];

/** Fill {name} placeholders without String.replace's `$` substitution patterns. */
function fill(template, vars) {
  let out = String(template);
  for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  return out;
}

/**
 * @param {unknown} text   a label from a chat block
 * @param {(key: string) => string} t   translator from useTranslation()
 * @param {string} language   active UI language
 */
export function localiseChatLabel(text, t, language) {
  if (typeof text !== 'string' || typeof t !== 'function' || !language || language === 'en') return text;
  const trimmed = text.trim();
  const key = EXACT_KEYS[trimmed];
  if (key) return t(key);
  for (const [rx, k, names] of PATTERNS) {
    const m = trimmed.match(rx);
    if (m) {
      const vars = {};
      names.forEach((name, i) => { vars[name] = m[i + 1]; });
      return fill(t(k), vars);
    }
  }
  return text;
}
